import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { 
  customerMessagingOperations, 
  customerRealtimeOperations 
} from '../utils/customerSupabase';

const noop = () => {};
const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  isOpen: false,
  toasts: [],
  addNotification: noop,
  addToast: noop,
  removeToast: noop,
  markAsRead: noop,
  markAllAsRead: noop,
  clearAll: noop,
  toggleOpen: noop,
  closePanel: noop,
  fetchUnreadCount: noop,
  pushPermission: null,
  requestPushPermission: noop,
});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [pushPermission, setPushPermission] = useState(
    () => (typeof Notification !== 'undefined' ? Notification.permission : null)
  );
  const clearedAtRef = useRef(0);

  const getClearKey = useCallback(
    () => (user?.id ? `customer_notifications_cleared_at_${user.id}` : null),
    [user?.id]
  );

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const count = await customerMessagingOperations.getUnreadCount(user.id);
      setUnreadCount(count || 0);
    } catch (err) {
      console.error('[Notifications] Error fetching unread count:', err);
    }
  }, [user?.id]);

  // Remove a toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Add a toast notification (duration 0 = stay until dismissed)
  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const duration = toast.duration ?? 5000;
    const newToast = {
      id,
      type: toast.type || 'info',
      title: toast.title,
      message: toast.message,
      duration,
      action: toast.action,
    };
    setToasts(prev => [...prev, newToast]);
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, duration);
    }
    return id;
  }, []);

  const requestPushPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !window.Notification) return null;
    try {
      const permission = await window.Notification.requestPermission();
      setPushPermission(permission);
      return permission;
    } catch (_) {
      return null;
    }
  }, []);

  // Auto-request push notifications when user is authenticated (enable by default)
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      requestPushPermission();
    }
  }, [isAuthenticated, user?.id, requestPushPermission]);

  // Add notification to the list; respect clear-all so old ones don't resurface
  const addNotification = useCallback((notification) => {
    const timestamp = notification?.timestamp ? new Date(notification.timestamp) : new Date();
    const clearedAt = clearedAtRef.current || 0;
    if (clearedAt && timestamp.getTime() <= clearedAt) return;

    const newNotification = {
      id: Date.now() + Math.random(),
      ...notification,
      timestamp,
      read: false,
    };
    setNotifications(prev => [newNotification, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);

    addToast({
      type: notification.type || 'info',
      title: notification.title,
      message: notification.message,
      duration: 0,
    });
    if (typeof window !== 'undefined' && window.Notification?.permission === 'granted' && document.hidden) {
      try {
        new window.Notification(notification.title || 'Mrs Cater', {
          body: notification.message || '',
          icon: '/favicon.ico',
        });
      } catch (_) {}
    }
  }, [addToast]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  // Clear all notifications; persist so they don't resurface on login/refresh
  const clearAll = useCallback(() => {
    const key = getClearKey();
    if (key && typeof window !== 'undefined') {
      const now = Date.now();
      try {
        window.localStorage.setItem(key, String(now));
      } catch (_) {}
      clearedAtRef.current = now;
    }
    setNotifications([]);
    setUnreadCount(0);
  }, [getClearKey]);

  // Toggle notification panel
  const toggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Set up real-time subscriptions when authenticated; load clearedAt so clear-all persists
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setNotifications([]);
      setUnreadCount(0);
      clearedAtRef.current = 0;
      return;
    }
    const key = getClearKey();
    if (key && typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem(key);
        clearedAtRef.current = raw ? (Number(raw) || 0) : 0;
      } catch (_) {}
    }

    const messagesChannel = customerRealtimeOperations.subscribeToMessages(
      user.id,
      (payload) => {
        addNotification({
          type: 'message',
          title: 'New Message',
          message: 'You have a new message from a caterer',
          data: payload.new
        });
      }
    );

    const ordersChannel = customerRealtimeOperations.subscribeToOrdersForCustomer(
      user.id,
      (payload) => {
        if (payload.eventType === 'UPDATE' && payload.new?.status) {
          const status = payload.new.status;
          const code = payload.new?.delivery_code;
          let message = `Your order status: ${status.replace(/_/g, ' ')}`;
          if (status === 'ready_for_delivery' && code) {
            message = `Your order is ready for delivery. Give the caterer this code: ${code}`;
          }
          addNotification({
            type: 'order',
            title: 'Order update',
            message,
            data: payload.new
          });
        }
      }
    );

    const platformChannel = customerRealtimeOperations.subscribeToPlatformNotifications(
      (row) => {
        addNotification({
          type: 'info',
          title: row?.title || 'Update from Mrs Cater',
          message: row?.body || '',
          data: row,
        });
      }
    );

    const bidsChannel = customerRealtimeOperations.subscribeToNewBids(
      async (payload, supabaseClient) => {
        const eventId = payload.new?.event_id;
        if (!eventId) return;
        const { data: event } = await supabaseClient
          .from('events')
          .select('customer_id')
          .eq('id', eventId)
          .single();
        if (event?.customer_id === user.id) {
          addNotification({
            type: 'bid',
            title: 'New bid on your request',
            message: 'A caterer has placed a bid on your special request',
            data: payload.new
          });
        }
      }
    );

    fetchUnreadCount();

    return () => {
      if (platformChannel) customerRealtimeOperations.unsubscribe(platformChannel);
      if (messagesChannel) customerRealtimeOperations.unsubscribe(messagesChannel);
      if (ordersChannel) customerRealtimeOperations.unsubscribe(ordersChannel);
      if (bidsChannel) customerRealtimeOperations.unsubscribe(bidsChannel);
    };
  }, [isAuthenticated, user?.id, getClearKey, addNotification, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    isOpen,
    toasts,
    addNotification,
    addToast,
    removeToast,
    markAsRead,
    markAllAsRead,
    clearAll,
    toggleOpen,
    closePanel,
    fetchUnreadCount,
    pushPermission,
    requestPushPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
